<template>
  <div class="flight-details">
    <div class="row align-items-center">
      <!-- Flight Details Column -->
      <div class="col-8">
        <div v-if="outboundFlight" class="mb-1">
          <p class="mb-0 caption">
            <span class="caption-strong">{{ t('reservations.outbound') }}</span> {{ outboundFlight.departureAirport }} <span class="caption-strong">{{ outboundFlight.departureTime }}</span> → {{ outboundFlight.arrivalAirport }} <span class="caption-strong">{{ outboundFlight.arrivalTime }}</span>
          </p>
        </div>
        <div v-if="inboundFlight" class="mb-1">
          <p class="mb-0 caption">
            <span class="caption-strong">{{ t('reservations.return') }}</span> {{ inboundFlight.departureAirport }} <span class="caption-strong">{{ inboundFlight.departureTime }}</span> → {{ inboundFlight.arrivalAirport }} <span class="caption-strong">{{ inboundFlight.arrivalTime }}</span>
          </p>
        </div>
        <p class="mb-0 caption">{{ getFlightClass() }} {{ t('reservations.class') }}</p>
      </div>
      
      <!-- Airline Logo Column -->
      <div class="col-4 d-flex align-items-start justify-content-center">
        <div class="airline-logos-container">
          <!-- Show both logos if different companies -->
          <template v-if="showBothLogos">
            <img 
              v-if="outboundFlight?.airlineCode" 
              :src="getAirlineIconUrl(outboundFlight.airlineCode)"
              :alt="`${outboundFlight.airlineCode} logo`"
              class="airline-logo mb-1"
              @error="handleImageError"
            />
            <img 
              v-if="inboundFlight?.airlineCode" 
              :src="getAirlineIconUrl(inboundFlight.airlineCode)"
              :alt="`${inboundFlight.airlineCode} logo`"
              class="airline-logo"
              @error="handleImageError"
            />
          </template>
          <!-- Show single logo if same company or only one flight -->
          <template v-else>
            <img 
              v-if="getPrimaryAirlineCode()" 
              :src="getAirlineIconUrl(getPrimaryAirlineCode())"
              :alt="`${getPrimaryAirlineCode()} logo`"
              class="airline-logo"
              @error="handleImageError"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { getAirportName } from '@/utils/locationCodes'

  // Initialize i18n
  const { t } = useI18n()

  // Helper function to get airline icon URL
  const getAirlineIconUrl = (airlineCode: string): string => {
    if (!airlineCode) return ''
    
    const normalizedCode = airlineCode.trim().toUpperCase()
    
    const imageMap: Record<string, () => string> = {
      BA: () => require('@/assets/carrier-logos/BA.png'),
      AF: () => require('@/assets/carrier-logos/AF.png'),
      FR: () => require('@/assets/carrier-logos/FR.png'),
      LH: () => require('@/assets/carrier-logos/LH.png'),
      LX: () => require('@/assets/carrier-logos/LX.png'),
      KL: () => require('@/assets/carrier-logos/KL.png'),
      LS: () => require('@/assets/carrier-logos/LS.png'),
      U2: () => require('@/assets/carrier-logos/U2.png'),
      W9: () => require('@/assets/carrier-logos/W9.png'),
      BY: () => require('@/assets/carrier-logos/BY.png'),
    }
    
    return imageMap[normalizedCode]?.() || ''
  }

  // Helper function to get the primary airline code (prefer outbound, fallback to inbound)
  const getPrimaryAirlineCode = (): string => {
    if (outboundFlight.value?.airlineCode) {
      return outboundFlight.value.airlineCode
    }
    if (inboundFlight.value?.airlineCode) {
      return inboundFlight.value.airlineCode
    }
    return ''
  }

  // Check if we should show both logos (different companies for outbound and inbound)
  const showBothLogos = computed(() => {
    return outboundFlight.value?.airlineCode && 
           inboundFlight.value?.airlineCode && 
           outboundFlight.value.airlineCode !== inboundFlight.value.airlineCode
  })

  // Define the props
  const props = defineProps<{
    tripInformation: any
  }>()

  // Get flight details object
  const flightDetail = computed(() => {
    if (Array.isArray(props.tripInformation) && props.tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      return props.tripInformation[0].details || props.tripInformation[0].tripInformation
    }
    return props.tripInformation
  })

  // Get outbound flight information
  const outboundFlight = computed(() => {
    const flight = flightDetail.value
    if (flight?.outboundTrip) {
      const departureAirport = getAirportName(flight.outboundTrip.departure?.location || '')
      const arrivalAirport = getAirportName(flight.outboundTrip.arrival?.location || '')
      const departureTime = flight.outboundTrip.departure?.time || ''
      const arrivalTime = flight.outboundTrip.arrival?.time || ''
      const airlineCode = flight.outboundTrip.company || ''
      return {
        departureAirport,
        arrivalAirport,
        departureTime,
        arrivalTime,
        airlineCode
      }
    }
    return null
  })

  // Get inbound flight information
  const inboundFlight = computed(() => {
    const flight = flightDetail.value
    if (flight?.inboundTrip) {
      const departureAirport = getAirportName(flight.inboundTrip.departure?.location || '')
      const arrivalAirport = getAirportName(flight.inboundTrip.arrival?.location || '')
      const departureTime = flight.inboundTrip.departure?.time || ''
      const arrivalTime = flight.inboundTrip.arrival?.time || ''
      const airlineCode = flight.inboundTrip.company || ''
      return {
        departureAirport,
        arrivalAirport,
        departureTime,
        arrivalTime,
        airlineCode
      }
    }
    return null
  })

  // Handle image loading errors
  const handleImageError = (event: Event) => {
    const img = event.target as HTMLImageElement
    img.style.display = 'none' // Hide the image if it fails to load
  }

  // Helper function to check if flight details are valid
  const hasFlightDetails = () => {
    const { tripInformation } = props
    // Handle array of tripInformation (most common case)
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const flightDetail = tripInformation[0].details || tripInformation[0].tripInformation
      return flightDetail && (flightDetail.outboundTrip || flightDetail.inboundTrip || flightDetail.class)
    }
    // Handle direct object (fallback)
    return tripInformation && (tripInformation.outboundTrip || tripInformation.inboundTrip || tripInformation.class)
  }

  // Helper function to get flight class
  const getFlightClass = () => {
    const flight = flightDetail.value
    
    if (flight?.class) {
      return flight.class
    } else if (flight?.outboundTrip?.class) {
      return flight.outboundTrip.class
    } else if (flight?.inboundTrip?.class) {
      return flight.inboundTrip.class
    }
    return 'Economy'
  }


</script>

<style scoped>
  .flight-details {
    padding: 0.25rem 0;
  }

  .airline-logos-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }

  .airline-logo {
    width: 120px;
    height: 40px;
    object-fit: contain;
    border-radius: 4px;
  }
</style> 